<template>
  <div
    id="app"
    :class="['route--' + view.toLowerCase(), isOnTop ? 'page--is-top' : 'page--is-scrolled']"
  >
    <div class="demo-indicator" v-if="isDemo">
      <FontAwesomeIcon icon="exclamation-circle" />
      <span>{{ $t('thisIsADemo') }}</span>
    </div>
    <div class="transition">
      <img class="transition__img" :src="logo.src || logo" />
    </div>
    <div
      v-if="$route.meta.navigation !== false"
      class="nav"
      :class="[
        `nav--${view.toLowerCase()}`,
        { [`nav--${view.toLowerCase()}-expanded`]: isExpanded },
      ]"
    >
      <div class="container">
        <NavigationComponent
          :logo="logo"
          :items="items"
          :title="projectName"
          padding="20px 0"
          :action="mainAction || redirect(['Project', 'Residential'], { scrollTo: 0 })"
          :isOpen.sync="isExpanded"
        />
      </div>
    </div>
    <transition mode="out-in" name="route">
      <router-view />
    </transition>
    <FooterComponent v-if="$route.meta.footer !== false" :navigation="items" />
    <AssetComponent />
  </div>
</template>

<script>
import { NavigationComponent } from 'vue-elder-navigation'
import { mapState, mapActions } from 'vuex'
import AssetComponent, { EventBus as AssetBus } from '@kvass/asset-manager'
import FooterComponent from '@/components/Footer'
import API from './api'
import BrowserApiMixin from '@/mixins/browser-api'

export default {
  mixins: [BrowserApiMixin('app')],
  data() {
    return {
      isOnTop: true,
      customItems: [],
      isExpanded: false,
    }
  },
  computed: {
    ...mapState('Root', ['item', 'promises']),
    logos() {
      return {
        light: API.logoInverted,
        dark: API.logo,
      }
    },
    isSingular() {
      return API.projectType === 'singular'
    },
    view() {
      if (!this.isSingular) return this.$path('name', this.$route) || ''
      return 'ProjectResidential'
    },
    logo() {
      return this.logos.dark
    },
    isDemo() {
      return this.item.demo || false
    },
    labels() {
      return KvassConfig.get('customLabels') || {}
    },
    projectName() {
      return this.item.name || ''
    },
    seo() {
      return this.$path('customFields.seo', this.item) || {}
    },
    hasFlatfinder() {
      return this.$path('flatfinders.length', this.item)
    },
    hasStatsTotal() {
      return this.$path('stats.total', this.item)
    },
    hasResidentials() {
      return this.$path('$store.state.Residentials.item.docs.length')
    },
    hasAttachments() {
      if (!this.isSingular) return this.$path('media.attachments.length', this.item)
      return this.$path('$store.state.Residential.item.media.attachments.length')
    },
    brochure() {
      return this.$path('media.brochure', this.item) || []
    },
    brochureStrategy() {
      return KvassConfig.get('siteSettings.brochureDownloadStrategy') || 'direct'
    },
    posts() {
      return this.$path('posts', this.item) || []
    },
    gallery() {
      if (!this.isSingular) return this.$path('media.gallery', this.item) || []
      return this.$path('$store.state.Residential.item.media.gallery') || []
    },
    items() {
      return [...this.defaultItems, ...this.customItems].sort((a, b) => a.order - b.order)
    },
    addons() {
      return this.$path('item.metadata.addons') || []
    },
    redirectUrl() {
      return (
        this.$path('item.publishing.redirectUrl') &&
        `https://${this.$path('item.publishing.redirectUrl')}`
      )
    },
    mainAction() {
      if (this.addons.includes('project:flatfinder')) return this.redirectUrl
      if (this.isSingular) return
      return this.redirect('Project', { scrollTo: 0 })
    },

    defaultItems() {
      return [
        ...this.posts.map(i => {
          if (!this.$path('customFields.show-in-navigation', i)) return {}

          return {
            label: i.title,
            exact: true,
            class: 'elder__navigation-component--item',
            action: this.redirect(['Post'], { params: { slug: i.slug }, forceRedirect: true }),
            order: this.$path('customFields.navigation-order', i) * 10 - 5,
          }
        }),
        {
          label: 'Kontakt',
          class: 'elder__navigation-component--item',
          action: this.redirect('Contact'),
          order: 100,
        },
      ]
        .filter(x => !('condition' in x) || x.condition)
        .filter(x => x.action || (x.items && x.items.length))
    },
  },
  methods: {
    ...mapActions('Root', ['fetch']),
    redirect(name, options = { forceRedirect: false }) {
      return async function() {
        name = name instanceof Array ? name : [name]

        let { action, scrollTo, hash, params, forceRedirect } = options

        if (!name.includes(this.$route.name) || params?.slug != this.$route.query?.slug)
          await this.$router.push({ name: name[0], hash, params })
        else if (hash) {
          let target = document.querySelector(
            '.scroll-anchor--type-anchor.scroll-anchor--value-' + hash.substring(1),
          )
          if (!target) return
          target.scrollIntoView({ behavior: 'smooth' })
        }
        if (action) return this.action()
        if (scrollTo !== undefined) window.scrollTo(0, scrollTo)
      }
    },
  },
  created() {
    const debounce = fn => {
      let frame
      return (...params) => {
        if (frame) cancelAnimationFrame(frame)
        frame = requestAnimationFrame(() => fn(...params))
      }
    }

    const storeScroll = () => {
      this.isOnTop = window.scrollY <= 5
    }

    document.addEventListener('scroll', debounce(storeScroll), { passive: true })
    storeScroll()

    this.promise = this.fetch()
  },
  metaInfo() {
    return {
      titleTemplate: this.projectName
        ? `%s | ${this.$path('seo.title') || this.projectName}`
        : `${this.$t('loading', { resource: null })}...`,
      link: [
        { rel: 'preload', href: this.logos.dark, as: 'image' },
        { rel: 'preload', href: this.logos.light, as: 'image' },
        { rel: 'icon', href: API.favicon, type: 'image/png' },
      ],
    }
  },
  components: {
    NavigationComponent,
    FooterComponent,
    AssetComponent,
  },
}
</script>

<style lang="scss">
@import '@/styles/main';

#app {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  position: relative;
}

.transition {
  @keyframes PageLoadTransition {
    0% {
      opacity: 1;
    }
    100% {
      opacity: 0;
      display: none;
      z-index: -1;
    }
  }
  @keyframes hideAnimation {
    to {
      display: none;
      width: 0;
      height: 0;
      z-index: -1;
    }
  }
  display: flex;
  align-items: center;
  justify-content: center;

  background-color: var(--secondary);
  position: fixed;
  height: 100vh;
  margin: 0;
  width: 100%;
  opacity: 1;
  z-index: 10000;

  animation: PageLoadTransition 1s 2s forwards, hideAnimation 0s forwards 3s;

  &__img {
    @keyframes PageLoadImage {
      0% {
        opacity: 0;
      }
      100% {
        opacity: 1;
      }
    }

    @keyframes hideAnimation {
      to {
        display: none !important;
        width: 0 !important;
        height: 0 !important;
      }
    }

    max-width: 14vw;
    width: 100%;
    min-width: 200px;
    min-height: 200px;
    object-fit: contain;
    opacity: 0;
    animation: PageLoadImage 2s 0s forwards, hideAnimation 0s forwards 3s;
  }
}

.nav {
  .container {
    max-width: initial;
  }
  position: sticky;
  top: 0;
  width: 100%;
  z-index: get-layer('nav');
  background: var(--secondary);

  .elder__navigation-bars {
    color: var(--primary);
    border-radius: 100%;
    padding: 14px;
    cursor: pointer;

    &:hover {
      background-color: var(--nav-hover-bars);
    }
  }

  .elder__navigation {
    padding: 0 !important;
    //padding-block: 0.75rem !important;

    &-component {
      color: var(--primary);
      font-weight: 400;
      cursor: pointer;
      border-radius: 10px;
      font-size: 18px;
      padding: 8px 16px 8px 15px;
      // margin-bottom: 0.5rem;
      position: relative;

      &--item {
        &:hover:after {
          content: '';

          bottom: -5px !important;
          left: 0px !important;

          width: 100% !important;
          height: 3px !important;
          background: var(--primary);
        }
      }
    }

    &-logo-image {
      max-height: 100px !important;

      cursor: pointer;
      @include respond-below('desktop') {
        max-height: 75px !important;
      }
      @include respond-below('phone') {
        max-height: 64px !important;
      }
    }
  }

  &--project-expanded,
  &--residential-expanded,
  &--contact-expanded,
  &--areals-expanded,
  &--post-expanded {
    padding-bottom: 1rem;

    background-color: hsla(var(--secondary-h), var(--secondary-s), var(--secondary-l), 0.9);

    backdrop-filter: blur(5px);

    height: 100vh;

    display: flex;

    & > .container {
      display: flex;
    }

    .elder__navigation {
      height: 100%;
    }

    .elder__navigation-actions {
      height: 85vh;
      display: flex !important;
      flex-direction: column;
      gap: 1rem;
      justify-content: center;
      align-items: center;
      font-size: 1.25rem;

      .elder__navigation-component {
        padding: 1rem 2.5rem;
        justify-content: center;
        font-size: 1.25rem;
        width: 100%;

        &:not(.elder__navigation-component--primary):hover:after {
          display: none;
        }
        &:not(.elder__navigation-component--primary):hover {
          background-color: var(--nav-hover-bars);
        }
      }
    }
  }
}

.demo-indicator {
  background-color: var(--warning);
  color: white;

  padding: 0.5em 0.75em;

  font-size: 0.8em;
  letter-spacing: 0.5px;
  line-height: 1;

  svg {
    margin-right: 5px;
  }
}

.route-enter,
.route-leave-to {
  opacity: 0;
  transform: scale(0.98);
}

.route-enter-to,
.route-leave {
  opacity: 1;
  transform: scale(1);
}

.route-enter-active,
.route-leave-active {
  transform-origin: top;
  transition: 500ms ease;
}
</style>
